/**
 * PROPRIETARY AND CONFIDENTIAL
 * Copyright © 2026 Contractor Co-Pilot. All Rights Reserved.
 *
 * Grounded in budgets-finance.md, invoicing.md, proposals.md, crm.md,
 * and suppliers-procurement.md Marketing Constraints.
 *
 * IP to protect:
 * - Dashboard metric formulas (internal)
 * - Revenue recognition resolution order (budget → org → default)
 * - MORS derivation
 * - pricing-intelligence/analytics.ts filename
 * - Specific confidence thresholds
 *
 * Do NOT:
 * - Claim "GAAP compliance" or name accounting frameworks
 * - Market "Grandmaster Line Items drill-down" — that tab is empty
 *   (Known Issue in budgets-finance.md)
 * - Promise budget actuals engine features that don't exist yet
 * - Reveal dashboard metric internals
 * - Market the 5-level pricing cascade internals
 */
"use client";

import {
  FeaturePageTemplate,
  type FeaturePageConfig,
} from "@/components/landing/feature-page-template";
import {
  Layers,
  TrendingUp,
  TrendingDown,
  Building2,
  Clock,
  DollarSign,
  Briefcase,
  Eye,
  Sparkles,
  PieChart,
  LineChart,
  Calculator,
  ClipboardList,
  Users,
} from "lucide-react";

const config: FeaturePageConfig = {
  badge: "Reporting & Analytics",
  title: "See where you are",
  titleHighlight: "making or losing money.",
  description:
    "Get clear answers to the questions that matter most: Which jobs are over budget? Which clients are slow to pay? Which divisions are actually profitable?",

  featureGrid: {
    title: "Numbers that tie out across every view",
    items: [
      {
        icon: Layers,
        title: "Budget Rollup Overlay",
        description:
          "Compare planned costs against real costs so you can catch margin leaks before close-out.",
      },
      {
        icon: TrendingUp,
        title: "Per-Division Variance",
        description:
          "See which divisions are winning and which ones need tighter control.",
      },
      {
        icon: PieChart,
        title: "Per-Cost-Type Variance",
        description:
          "Break variance down by materials, labor, equipment, and subs so you know where overruns start.",
      },
      {
        icon: LineChart,
        title: "Pipeline Conversion Metrics",
        description:
          "Track conversion from estimate to signed work so you can spot where deals stall.",
      },
      {
        icon: Eye,
        title: "Proposal Engagement Analytics",
        description:
          "See when clients open and review proposals so follow-ups happen at the right time.",
      },
      {
        icon: Clock,
        title: "Invoice Aging",
        description:
          "Know exactly who owes you now, 30 days out, and 60+ days out.",
      },
      {
        icon: Building2,
        title: "Grandmaster Budget Rollup",
        description:
          "View company-wide financial performance while still drilling into each division.",
      },
      {
        icon: Sparkles,
        title: "Pricing Signals Flywheel",
        description:
          "Use real purchase and job outcomes to improve the next estimate.",
      },
      {
        icon: Users,
        title: "Client Metrics",
        description:
          "Track client value, win rates, and average job size to prioritize better opportunities.",
      },
    ],
  },

  workflow: {
    title: "What reporting actually looks like in CC",
    description:
      "A practical reporting rhythm your team can use weekly, not just at month-end.",
    steps: [
      {
        label: "Plan Vs Actual",
        title: "Budget rollup overlays reality on your plan",
        description:
          "Open a budget and quickly see where actual job costs are above or below plan. Review by division and cost bucket, then decide where to intervene.",
        details: [
          "Planned vs actual view in one place",
          "Division-level and cost-type-level variance",
          "Revenue and cost context side by side",
          "Approved change orders reflected in totals",
          "Built for weekly operating reviews",
        ],
      },
      {
        label: "Sales Pipeline",
        title: "Pipeline metrics run on estimate status",
        description:
          "Track how estimates move through your pipeline so sales managers can focus effort where wins are most likely.",
        details: [
          "Stage conversion visibility",
          "Time-in-stage trends",
          "Stage value totals",
          "Win/loss patterns by team and division",
          "Better follow-up timing decisions",
        ],
      },
      {
        label: "Proposal Engagement",
        title: "Know when clients are reading — and when they're not",
        description:
          "See if clients opened and reviewed your proposal before you call, so follow-ups are better timed and more effective.",
        details: [
          "Proposal opens and activity indicators",
          "Time-to-decision visibility",
          "Win rate trend support",
          "Average proposal value by team",
          "Follow-up prioritization cues",
        ],
      },
      {
        label: "Cash Flow",
        title: "Invoice aging and payment status",
        description:
          "Review receivables by aging bucket to protect cash flow and escalate collections before they become write-offs.",
        details: [
          "Current, 1-30, 31-60, 61-90, and 90+ views",
          "Client-level outstanding totals",
          "Invoice overdue visibility",
          "Payment history context",
          "Collection prioritization support",
        ],
      },
      {
        label: "Learn",
        title: "Pricing signals show your cost database getting sharper",
        description:
          "Use completed-job and purchasing outcomes to tighten future pricing so bids stay competitive without giving away margin.",
        details: [
          "Estimate vs actual variance trend tracking",
          "Category-level pricing drift visibility",
          "Recurring overrun pattern detection",
          "Cost database update guidance",
          "Support for continuous pricing improvement",
        ],
      },
    ],
  },

  capabilities: [
    {
      icon: Layers,
      title: "One Source of Financial Truth",
      description:
        "When teams review pipeline, budget, and receivables, everyone should be looking at the same numbers. This reporting layer is built to keep leadership, PMs, and finance aligned.",
      bullets: [
        "Consistent totals across core financial views",
        "Budget, pipeline, and invoicing alignment",
        "Fewer spreadsheet reconciliations",
        "Faster weekly leadership reviews",
        "More confidence in decision-making",
      ],
    },
    {
      icon: TrendingDown,
      title: "Plan vs Actual Without Losing the Plan",
      description:
        "You need to preserve your original budget targets while still seeing real performance. This gives you both, clearly.",
      bullets: [
        "Original budget context stays visible",
        "Actual performance layered on top",
        "Variance visibility by cost area",
        "Change order impact included",
        "Better post-job learning",
      ],
    },
    {
      icon: Building2,
      title: "Division-Level Control, Company-Level Visibility",
      description:
        "If you run multiple divisions, you can evaluate each one on its own while still seeing full-company performance.",
      bullets: [
        "Independent division financial visibility",
        "Company-wide rollup view",
        "Quick drill-down by division",
        "Cost-type variance context",
        "Easier executive reporting",
      ],
    },
    {
      icon: Sparkles,
      title: "Continuous Pricing Improvement",
      description:
        "Use real-world outcomes to improve estimate quality over time instead of relying on stale assumptions.",
      bullets: [
        "Outcome-based pricing insights",
        "Variance trend visibility",
        "Category-level tuning support",
        "Better future bid confidence",
        "Continuously improving estimate accuracy over time",
      ],
    },
  ],

  connections: {
    title: "How reporting connects to the rest of CC",
    items: [
      {
        icon: Calculator,
        flow: "Estimate → Pipeline Metrics",
        description:
          "Estimate status progression is the pipeline. Metrics reflect the same records your team is actively updating.",
      },
      {
        icon: Briefcase,
        flow: "Project Actuals → Budget Overlay",
        description:
          "Project cost outcomes are reflected in reporting views so teams can compare plan versus actual by cost type.",
      },
      {
        icon: DollarSign,
        flow: "Invoice Payment → Recognized Revenue",
        description:
          "Payments feed the budget rollup under your chosen revenue recognition policy. Income budget lines show recognized revenue per division.",
      },
      {
        icon: Eye,
        flow: "Proposal View → Engagement Metrics",
        description:
          "Proposal engagement activity feeds reporting, including win rate and time-to-decision trends.",
      },
    ],
  },

  relatedFeatures: [
    {
      label: "Budgets & Invoices",
      href: "/features/budgets-invoices",
      icon: DollarSign,
    },
    {
      label: "Project Management",
      href: "/features/project-management",
      icon: Briefcase,
    },
    {
      label: "Estimates & Takeoffs",
      href: "/features/estimates",
      icon: Calculator,
    },
    {
      label: "Proposals & Client Portal",
      href: "/features/proposals-portal",
      icon: ClipboardList,
    },
    {
      label: "CRM",
      href: "/features/crm",
      icon: Users,
    },
    {
      label: "Divisions",
      href: "/features/divisions",
      icon: Building2,
    },
  ],
};

export function ReportingPageClient() {
  return <FeaturePageTemplate config={config} />;
}
